//=================================================
// Navigation
//=================================================

// Add dropdown option to link rails if not all options can fit
import { createInitializeOnce } from './utilities';
import { getReducedMotion } from './user-preferences';

const initializeOnce = createInitializeOnce();

// Use intersection observers to tell when we need to add arrows because content is hidden
function updateLinkRailArrows(rail) {
    const list = rail.querySelector('.link-rail__list');
    if (!list) return;
    rail.classList.toggle('left', list.scrollLeft > 0);
    rail.classList.toggle('right', Math.ceil(list.scrollLeft) + list.clientWidth < list.scrollWidth);
}

function adjustLinkRail(rail) {
    const list = rail.querySelector('.link-rail__list');
    if (!list) return;
    list.addEventListener('scroll', () => updateLinkRailArrows(rail), { passive: true });
    updateLinkRailArrows(rail);
}

// Set up or update link rails (which could be added dynamically)
export function adjustLinkRails() {
    // Add listeners to link rails
    document.querySelectorAll('.link-rail').forEach((rail) => {
        if (initializeOnce(rail)) {
            const list = rail.querySelector('.link-rail__list');
            if (!list) return;

            // Click to scroll left
            rail.querySelectorAll('.link-rail__left').forEach((el) => {
                el.addEventListener('click', (e) => {
                    e.preventDefault();
                    list.scrollTo({ left: list.scrollLeft - (list.offsetWidth * 33.33), behavior: 'smooth' });
                });
            });

            // Click to scroll right
            rail.querySelectorAll('.link-rail__right').forEach((el) => {
                el.addEventListener('click', (e) => {
                    e.preventDefault();
                    list.scrollTo({ left: list.scrollLeft + (list.offsetWidth * 33.33), behavior: 'smooth' });
                });
            });

            // Activate clicked tab (if it's a link rail of tabs)
            if (rail.classList.contains('link-rail--tabs')) {
                const tabs = list.querySelectorAll('.link-rail-item');
                tabs.forEach((el) => {
                    el.addEventListener('click', () => {
                        tabs.forEach(ele => ele.classList.remove('active'));
                        el.classList.add('active');
                    });
                });
            }

            adjustLinkRail(rail);
        } else {
            updateLinkRailArrows(rail);
        }
    });
}

// Close all secondary navs
export function navigationSecondaryClose() {
    // Close all open secondary navs
    document.querySelectorAll('.primary-nav-item').forEach((el) => {
        el.classList.remove('open');
    });

    // Reset all aria-expanded elements
    document.querySelectorAll('.primary-nav [aria-expanded="true"]').forEach((el) => {
        el.setAttribute('aria-expanded', 'false');
    });

    // Add inactive class back if no primary nav links are active (i.e. on the homepage)
    // if (!document.querySelector('.primary-nav-item.active')) {
    //     document.querySelector('.primary-nav')?.classList.add('primary-nav--inactive');
    // }
}

// Reset all open elements of the navs
export function navigationClose() {
    navigationSecondaryClose();

    // Close native dialog
    document.querySelector('.primary-nav-wrapper')?.close();
}

// Open a primary nav item's secondary nav, closing any others
function navigationSecondaryOpen(item) {
    navigationSecondaryClose();
    item.classList.add('open');
    item.querySelector('.primary-nav-link--toggle')?.setAttribute('aria-expanded', 'true');

    // Focus on input if search
    if (item.classList.contains('primary-nav-item--search')) {
        item.querySelector('input')?.focus();
    }
}

// Open the site search
function navigationSearchOpen() {
    const item = document.querySelector('.primary-nav-item--search');
    if (!item) return;

    // On small screens the nav lives in a modal dialog that must be shown first
    const dialog = document.querySelector('.primary-nav-wrapper');
    if (window.isNavSmall && dialog && !dialog.open) {
        dialog.showModal();
    }

    navigationSecondaryOpen(item);
}

// CMD+K / CTRL+K opens search
function handleNavigationKeydown(e) {
    if ((e.metaKey || e.ctrlKey) && e.key === 'k') {
        e.preventDefault();
        navigationSearchOpen();
    }
}

function handleNavigationClicks(e) {
    if (!e.target.closest('.navigation')) {
        return;
    }

    const primaryToggle = e.target.closest('.primary-nav-link--toggle');
    const secondaryLink = e.target.closest('.secondary-nav-link');
    const searchClear = e.target.closest('.input__clear--search');
    const searchSubmit = e.target.closest('.search .input__submit');
    const menuTop = e.target.closest('.mini-header__top');
    const menuSmall = e.target.closest('.mini-header__menu');
    const menuClose = e.target.closest('.mini-header__close');
    const menuSkip = e.target.closest('.skip-to-content');

    const primary = document.querySelector('.primary-nav-wrapper');
    const primaryItems = primary?.querySelectorAll('.primary-nav-item');

    const rect = primary.getBoundingClientRect();
    const insideOverlay = (rect.top <= e.clientY && e.clientY <= rect.top + rect.height &&
        rect.left <= e.clientX && e.clientX <= rect.left + rect.width);

    // Note: only close navigation on page load for better feeling UX
    if (primaryToggle) {
        // Handle primary navigation clicks
        const item = primaryToggle.closest('.primary-nav-item');
        const alreadyOpen = item.classList.contains('open');

        navigationSecondaryClose();

        // Open new secondary nav if existing open nav wasn't selected
        if (!alreadyOpen) {
            // primary.classList.remove('primary-nav--inactive'); // This is necessary for homepage nav item hover behavior
            navigationSecondaryOpen(item);
        }

    } else if (secondaryLink) {
        // Handle secondary link clicks
        primaryItems.forEach((otherItem) => {
            otherItem.classList.remove('active');
        });
        const item = e.target.closest('.primary-nav-item');
        item.classList.add('active');

    } else if (searchClear) {
        // Handle clicks on search input clears (which only clear that input)
        const input = searchClear.parentElement.querySelector('input');
        if (input) {
            input.value = '';
        }
        const searchNavButton = document.querySelector('.primary-nav-item--search.open');
        if (searchNavButton) {
            searchNavButton.classList.remove('open');
        }

        // On large screens only close the navigation on search clear (on small just clear the input)
        if (!(window.isNavSmall && searchClear.classList.contains('input__clear--nav'))) {
            navigationClose();
        }

    } else if (searchSubmit) {
        // Handle search submission
        e.preventDefault();

        const input = searchSubmit.parentElement.querySelector('input');
        if (input.value) {
            Turbo.visit(`/search?q%5Bsearch_cont%5D=${ encodeURIComponent(input.value) }`);
        } else {
            Turbo.visit('/search');
        }

    } else if (menuSmall) {
        // Handle small menu opening
        e.preventDefault();
        e.stopPropagation();
        primary.showModal();

    } else if (menuTop) {
        // Scroll to top if "menu" is clicked on large screens
        const behavior = getReducedMotion() ? 'instant' : 'smooth';
        window.scrollTo({ top: 0, behavior: behavior });

    } else if (menuSkip) {
        // Accessible "skip to main content" link
        // Adapts learnings from https://axesslab.com/skip-links/
        const main = document.getElementById('main');
        if (main) {
            // Make main temporarily tabbable
            main.setAttribute('tabindex', '-1');
            main.addEventListener('blur', () => {
                main.setAttribute('tabindex', null);
            }, { once: true });
            main.focus();
        }

    } else if (!insideOverlay || menuClose) {
        navigationClose();
    }
}

// Show/hide "Menu" button based on scroll position
export function setupScrollableMenu() {
    const menuTop = document.querySelector('.mini-header__top');
    const primary = document.querySelector('.primary-nav');
    // Set scrollable menu behavior
    const callback = (entries) => {
        entries.forEach((entry) => {
            // When the primary nav scrolls out of view we need to do some things...
            // Check for scrollY to handle an momentary issue when Turbo replaces the page
            if ((entry.intersectionRatio === 0) && (window.scrollY > 0)) {
                // If the menu button is not at all active, activate it
                if (!menuTop.classList.contains('active') && !menuTop.classList.contains('will-be-active')) {
                    menuTop.classList.add('will-be-active');

                    setTimeout(() => {
                        menuTop.classList.add('active');
                    }, getReducedMotion() ? 0 : window.transitionDuration);
                }

            } else if (entry.intersectionRatio === 1) {
                // If the menu button is at all active, inactivate it
                if (menuTop.classList.contains('active') || menuTop.classList.contains('will-be-active')) {
                    menuTop.classList.remove('active');

                    setTimeout(() => {
                        menuTop.classList.remove('will-be-active');
                    }, getReducedMotion() ? 0 : window.transitionDuration);
                }

                // Drop any fragments from the url from jumplinks
                if (window.location.hash && document.readyState === 'complete') {
                    window.history.replaceState(window.history.state, document.title, `${ window.location.pathname }${ window.location.search }`);
                }
            }
        });
    };
    const observer = new IntersectionObserver(callback, { threshold: [0, 1.0] });
    observer.observe(primary);
}

export function setupAnimatedButtons() {
    const animateButton = (animatedButton) => {
        if (!initializeOnce(animatedButton)) {
            return;
        }

        animatedButton.addEventListener('animationstart', () => {
            animatedButton.setAttribute('data-animation', '1');
        });

        animatedButton.addEventListener('animationend', () => {
            animatedButton.setAttribute('data-animation', '0');
        });

        if (!getReducedMotion()) {
            animatedButton.classList.add('animate');
        }

        animatedButton.addEventListener('mouseenter', () => {
            if (getReducedMotion()) {
                return;
            }

            // Check if the animation is still running
            if (animatedButton.dataset.animation === '1') {
                // Add a one time listener for when it's done, then re-animate
                animatedButton.addEventListener('animationend', () => {
                    animatedButton.classList.remove('animate');

                    setTimeout(() => {
                        animatedButton.classList.add('animate');
                    }, 1);
                }, { once: true });

            } else {
                // Re-animate immediately
                animatedButton.classList.remove('animate');

                setTimeout(() => {
                    animatedButton.classList.add('animate');
                }, 10);
            }
        });
    };

    function setupBiennial26Ticker(button) {
        const inner = button.querySelector('.animated-btn__inner');

        if (!inner) return;

        const DURATION = 5000;

        // Animate quickly at first and lengthen slide 5 to compensate
        const keyframes = [
            { offset: 0 / 32, transform: 'translateX(0)' },
            { offset: 1 / 32, transform: 'translateX(0)' },
            { offset: 3 / 32, transform: 'translateX(-100%)' },
            { offset: 9 / 32, transform: 'translateX(-100%)' },
            { offset: 11 / 32, transform: 'translateX(-200%)' },
            { offset: 17 / 32, transform: 'translateX(-200%)' },
            { offset: 19 / 32, transform: 'translateX(-300%)' },
            { offset: 25 / 32, transform: 'translateX(-300%)' },
            { offset: 27 / 32, transform: 'translateX(-400%)' },
            { offset: 32 / 32, transform: 'translateX(-400%)' },
        ];

        let anim = null;

        const makeAnimation = (iterations) => {
            if (getReducedMotion()) return;

            if (anim) anim.cancel();

            anim = inner.animate(keyframes, {
                duration: DURATION,
                easing: 'linear',
                fill: 'forwards',
                iterations
            });

            anim.play();
        };

        const startLooping = () => {
            if (!anim || anim.playState !== 'running') {
                makeAnimation(Infinity);
            } else {
                anim.effect.updateTiming({ iterations: Infinity });
            }
        };

        const stopAnimation = () => {
            if (!anim) return;

            anim.pause();
            anim.currentTime = 0;
        };

        // Disable hover behavior on mobile, just play through once when menu is opened
        if (window.innerWidth <= 799) {
            document.querySelector('.mini-header__menu')?.addEventListener('click', () => makeAnimation(1));

            return;
        } else {
            makeAnimation(1);
        }

        button.addEventListener('mouseenter', startLooping);
        button.addEventListener('mouseleave', stopAnimation);
    }

    // Button text animation
    document.querySelectorAll('.animated-btn').forEach((animatedButton) => {
        if (animatedButton.classList.contains('btn--biennial26')) {
            setTimeout(() => {
                setupBiennial26Ticker(animatedButton);
            }, 750);
        } else {
            animateButton(animatedButton);
        }
    });

}

function activateNavLink(el) {
    const item = el.closest('.secondary-nav-item');
    item.classList.add('active');
    item.closest('.primary-nav-item')?.classList.add('active');
}

function deactivateNavLink(el) {
    el.closest('.secondary-nav-item')?.classList.remove('active');
}

// Update active states in navigation based on url
export function updateNavigation() {
    const path = window.location.pathname.toLowerCase();
    const primarySegment = path.split('/')[1];
    const primaryNav = document.querySelector('.primary-nav');
    let isActive = false;

    // if (primarySegment) {
    const primaryNavItems = primaryNav?.querySelectorAll('.primary-nav-item');
    primaryNavItems.forEach((el) => el.classList.remove('active'));

    // Don't bother checking on root
    if (path !== '/') {
        // Check for exact matches
        const secondaryNavLinks = document.querySelectorAll('.secondary-nav-link');
        secondaryNavLinks.forEach((el) => {
            if (el.getAttribute('href') === path) {
                isActive = true;
                activateNavLink(el);
            } else {
                deactivateNavLink(el);
            }
        });

        // If nothing is active, go through and check by startsWith instead (but just for highlighting the top level section)
        if (!isActive) {
            secondaryNavLinks.forEach((el) => {
                // Stop checking after the first match
                if (isActive) {
                    return
                }

                const primaryNavItem = el.closest('.primary-nav-item');
                if (el.getAttribute('href').startsWith(`/${ primarySegment }`)) {
                    isActive = true;
                    primaryNavItem.classList.add('active');
                }
            });
        }
    }

    primaryNav.classList.toggle('active', isActive);
}

// Visually we change treatment on hovering if there are no active items
// Only treat it as "hovering" when an individual link is being hovered
function handleNavigationMouseOver(e) {
    const hoveringLink = !!e.target.closest('.primary-nav-link');
    e.currentTarget.closest('.primary-nav')?.classList.toggle('hovering', hoveringLink);
}

function handleNavigationMouseOut(e) {
    e.target.closest('.primary-nav')?.classList.remove('hovering');
}

function trackNav(selector, label) {
    if (useAnalytics) {
        document.querySelectorAll(selector).forEach((link) => {
            // Fallback to text if a value for tracking isn't explicitly set
            let { value } = link.dataset;
            if (!value) {
                value = link.innerText;
            }
            value = value.toLowerCase().trim();

            link.addEventListener('click', () => {
                dataLayer.push({
                    event: 'navigation',
                    label,
                    value,
                });
            });
        });
    }
}

export function setupNavigation() {
    const navigation = document.querySelector('.navigation');
    if (navigation) {
        updateNavigation();

        if (initializeOnce(navigation)) {
            // Watch for the primary nav opening/closing
            const dialog = navigation.querySelector('.primary-nav-wrapper');
            const observer = new MutationObserver((mutations) => {
                mutations.forEach((mutation) => {
                    if (mutation.type === 'attributes' && mutation.attributeName === 'open') {
                        // Update aria-expanded on toggles
                        navigation.querySelectorAll('.mini-header__menu').forEach((el) => {
                            el.setAttribute('aria-expanded', dialog.hasAttribute('open').toString());
                        });

                    }
                });
            });
            observer.observe(dialog, { attributes: true });

            // Clicks
            navigation.removeEventListener('click', handleNavigationClicks);
            navigation.addEventListener('click', handleNavigationClicks);

            // Keyboard shortcuts (document-level, bound once)
            if (initializeOnce(document)) {
                document.addEventListener('keydown', handleNavigationKeydown);
            }

            // Hovers
            const primaryNavList = navigation.querySelector('.primary-nav__list');
            primaryNavList.removeEventListener('mouseover', handleNavigationMouseOver);
            primaryNavList.addEventListener('mouseover', handleNavigationMouseOver);
            primaryNavList.removeEventListener('mouseleave', handleNavigationMouseOut);
            primaryNavList.addEventListener('mouseleave', handleNavigationMouseOut);

            // Analytics
            trackNav('.primary-nav-link', 'primary');
            trackNav('.secondary-nav-link', 'secondary');
            trackNav('.link-rail--tertiary a', 'tertiary');
            trackNav('.link-rail--jumplinks a', 'jumplinks');
            trackNav('.link-rail--tabs a', 'tabs');
            trackNav('.footer a', 'footer');
            trackNav('.breadcrumbs__link', 'breadcrumbs');
            trackNav('.mini-header__top, .mini-header__menu', 'menu');
            trackNav('.mini-header__hours, .primary-nav__hours', 'hours');
            trackNav('.nav-action:not(.nav-action--default)', 'actions');
            trackNav('.nav-action--default', 'mini button');

            // Run on initial load
            adjustLinkRails();
            setupAnimatedButtons();
            setupScrollableMenu();
        }
    }
}
